import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import ReactFlow, {
  Background,
  Connection,
  ConnectionMode,
  Controls,
  Edge,
  Handle,
  MarkerType,
  Node,
  Position,
  ReactFlowInstance,
  ReactFlowProvider,
  addEdge,
  getSmoothStepPath,
  getStraightPath,
  useEdgesState,
  useNodesState,
  useReactFlow,
  useStore,
} from 'reactflow';
import 'reactflow/dist/style.css';
import { Button } from 'src/lib/components/Button.component';
import { fdelete, fget, fput } from 'src/lib/fetch';
import { DamNode } from 'src/lib/components/DamNode';
import { BranchNode } from 'src/lib/components/BranchNode';

import { DeviceEntity } from 'src/modules/dashboard/dto/device/device.entity';
import { ResponseDocument, ResponseListDocuments } from 'src/modules/dashboard/dto/response';

import { getEdgeParams } from './utils';
import { getRandomArbitrary } from 'src/lib/math/math';
import { on } from 'events';

export function StepFloatingEdge({ id, source, target, markerEnd, style, fromX, fromY, toX, toY }: any) {
  const sourceNode = useStore(useCallback((store) => store.nodeInternals.get(source), [source]));
  const targetNode = useStore(useCallback((store) => store.nodeInternals.get(target), [target]));

  if (!sourceNode || !targetNode) {
    return null;
  }

  const { sx, sy, tx, ty, sourcePos, targetPos } = getEdgeParams(sourceNode, targetNode);

  const [edgePath] = getSmoothStepPath({
    sourceX: sx,
    sourceY: sy,
    sourcePosition: sourcePos,
    targetPosition: targetPos,
    targetX: tx,
    targetY: ty,
    borderRadius: 0.5,
  });

  return <path id={id} className="react-flow__edge-path" d={edgePath} markerEnd={markerEnd} style={style} />;
}

export const DeviceNode = ({
  data:device,
}: {
  data: ResponseDocument<DeviceEntity> & {
    setDeviceNodes: React.Dispatch<React.SetStateAction<Node[]>>;
    onDelete: () => void;
  };
}) => {

  return (
    <div className="group relative flex w-40 flex-col border border-sky-400 text-center">
      <span className="inline-flex items-center justify-center bg-sky-700 p-1 text-white">{device.DEVICE_NAME.S}</span>
      <div className="flex h-full flex-col justify-center bg-slate-100 px-2 py-1 text-xs">
        <span className="text-center">{device.AREA_NAME.S}</span>
        <span className="text-center">{device?.RIVER_NAME?.S}</span>
      </div>

      <Handle
        className="h-2 w-2 border-none bg-slate-500 opacity-0 group-hover:opacity-75"
        style={
          {
            // clipPath: 'polygon(50% 0%, 100% 50%, 50% 100%, 0% 50%)',
          }
        }
        position={Position.Left}
        type="source"
      />
      <Handle
        className="h-2 w-2 border-none bg-slate-500 opacity-0 group-hover:opacity-75"
        style={
          {
            // clipPath: 'polygon(50% 0%, 100% 50%, 50% 100%, 0% 50%)',
          }
        }
        position={Position.Right}
        type="source"
      />
      <Handle
        className="h-2 w-2 border-none bg-slate-500 opacity-0 group-hover:opacity-75"
        style={
          {
            // clipPath: 'polygon(50% 0%, 100% 50%, 50% 100%, 0% 50%)',
          }
        }
        position={Position.Top}
        type="source"
      />
      <Handle
        className="h-2 w-2 border-none bg-slate-500 opacity-0 group-hover:opacity-75"
        style={
          {
            // clipPath: 'polygon(50% 0%, 100% 50%, 50% 100%, 0% 50%)',
          }
        }
        position={Position.Bottom}
        type="source"
      />

      <div
        className="absolute right-0 hidden bg-white p-1 text-red-600 transition-all ease-in-out hover:bg-red-500 hover:text-red-200 group-hover:block dark:text-white"
        title="Click to remove"
        onClick={() => device.onDelete() }>
        <svg className="h-1 w-1" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 14 14">
          <path
            stroke="currentColor"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            d="m1 1 6 6m0 0 6 6M7 7l6-6M7 7l-6 6"
          />
        </svg>
      </div>
    </div>
  );
};

function FloatingConnectionLine({ toX, toY, fromPosition, toPosition, fromNode }: any) {
  if (!fromNode) {
    return null;
  }

  const targetNode = {
    id: 'connection-target',
    width: 1,
    height: 1,
    positionAbsolute: { x: toX, y: toY },
  };

  const { sx, sy } = getEdgeParams(fromNode, targetNode as Node);
  const [edgePath] = getStraightPath({
    sourceX: sx,
    sourceY: sy,
    // sourcePosition: fromPosition,
    // targetPosition: toPosition,
    targetX: toX,
    targetY: toY,
    // borderRadius: 0.5,
  });

  return (
    <g>
      <path fill="none" stroke="#222" strokeWidth={1.5} d={edgePath} />
    </g>
  );
}

const ReactFlowContext = forwardRef<ReactFlowInstance, any>((_, ref) => {
  const reactFlow = useReactFlow();

  useImperativeHandle(ref, () => reactFlow);

  return null;
});

const addNode = (type: 'damNode' | 'branchNode' | 'device', data: any, setState: React.Dispatch<React.SetStateAction<Node[]>>) => {
  setState((prev) => {
    const exists = prev.some((node) => node.id === data.id); // Kiểm tra trùng lặp
    if (exists) {
      console.log(`${type} already exists:`, data.id);
      return prev; 
    }
    console.log(`Adding new ${type}:`, data);
    
    const newNode = {
      id: data.id || `${type}-${Date.now()}`,
      type,
      position: data.position || { x: getRandomArbitrary(-100, 100), y: getRandomArbitrary(-100, 100) },
      data: data.data || {},
    };

    console.log(`Adding new ${type}:`, newNode);
    return [...prev, newNode];
  });
};

const handleDeleteNode = (nodeId: string, type: 'damNode' | 'branchNode' | 'device', setState: React.Dispatch<React.SetStateAction<Node[]>>) => {
  setState((nodes) => nodes.filter((node) => node.id !== nodeId)); 
};

const AreaFlowChartPage = () => {
  const mark = document.getElementsByClassName('react-flow__panel react-flow__attribution bottom right').item(0);
  if (mark) mark.innerHTML = '';

  const reactFlowRef = useRef<ReactFlowInstance>(null);
  const [devices, setDevices] = useState<ResponseDocument<DeviceEntity>[]>([]);

  const [damNodes, setDamNodes] = useNodesState([]);
  const [deviceNodes, setDeviceNodes] = useNodesState([]);
  const [branchNodes, setBranchNodes] = useNodesState([]);

  const [deletedNodes, setDeletedNodes] = useState<Node[]>([]);
  const [deletedEdges, setDeletedEdges] = useState<Edge[]>([]);

  console.log('deletedNodes', deletedNodes);
  console.log('deletedEdges', deletedEdges);
  
  const { id } = useParams();

  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);

  const onConnect = useCallback(
    (params: Connection) => {
      const newEdge = {
        ...params,
        id: `edge-${Date.now()}`, 
      };
      setEdges((eds) => addEdge(newEdge, eds));
    },
    [setEdges],
  );


  const nodeTypes = useMemo(() => ({ device: DeviceNode, damNode: DamNode, branchNode: BranchNode }), []);
  const edgeTypes = useMemo(() => ({ floating: StepFloatingEdge }), []);
  console.log('nodes', nodes);
  console.log('edges', edges);
  console.log('damNodes', damNodes);

  useEffect(() => {
    setNodes((prev) => {
      
      const newNode = [];
      newNode.push(...deviceNodes);
      newNode.push(...damNodes);
      newNode.push(...branchNodes);

      console.log('newVal', newNode);
      return newNode || prev;
    });
  }, [deviceNodes, damNodes, branchNodes]);

  useEffect(() => {
    fget<any, ResponseListDocuments<DeviceEntity>>(`/api/deviceByArea?AREA_NAME=${id}`).then(([_, data]) => {
      if (data.Items) {
        setDevices(data.Items);
      }
    });
  }, []);

  useEffect(() => {
    // Fetch edges từ API mới
    fget<any, Edge[]>(`/api/areas/${id}/edges`).then(([_, data]) => {
      if (data) {
        console.info('edges fetch:', data);
        const formattedEdges = data.map((edge: any) => ({
          id: edge.EDGE_ID.S,
          source: edge.SOURCE.S,
          target: edge.TARGET.S,
        }));
        setEdges((prevEdges) => {
          const existingEdges = prevEdges || [];
          return [...existingEdges, ...formattedEdges];
        }); // Cập nhật edges vào state
      }
    });
  }, []);

  useEffect(() => {
    // Fetch nodes từ API mới
    fget<any, Node[]>(`/api/areas/${id}/nodes`).then(([_, data]) => {
      if (data) {
        const newBranchNodes: Node[] = [];
        const newDamNodes: Node[] = [];
        const newDeviceNodes: Node[] = [];

        const formattedNodes = data.map((node: any) => {
          const formattedNode = {
            id: node.NODE_ID.S,
            type: node.TYPE.S,
            position: {
              x: parseFloat(node.POSITIONS.S.split(',')[0]),
              y: parseFloat(node.POSITIONS.S.split(',')[1]),
            },
            data: JSON.parse(node.DATA.S || '{}'),
          };
          console.log('formattedNode', formattedNode);
          console.log('formattedNode', formattedNode.data);
          
          // Phân loại node
          if (node.TYPE.S === 'branchNode') {
            formattedNode.data = {
              ...formattedNode.data,
              onDelete: () => {
                // delete edges related to this node
                setEdges((prevEdges) => {
                  const currentDeletedEdges = prevEdges.filter((edge) => edge.source === node.NODE_ID.S || edge.target === node.NODE_ID.S);
                  setDeletedEdges((prevDeleted) => [...prevDeleted, ...currentDeletedEdges]);
                  return prevEdges.filter((edge) => edge.source !== node.NODE_ID.S && edge.target !== node.NODE_ID.S);
                });
                // delete node
                setDeletedNodes((prevDeleted) => [...prevDeleted, formattedNode]); 
                handleDeleteNode(node.NODE_ID.S, 'branchNode', setBranchNodes)
              },
            };
            newBranchNodes.push(formattedNode);
          } else if (node.TYPE.S === 'damNode') {
            formattedNode.data = {
              ...formattedNode.data,
              onDelete: () => {
                setDeletedNodes((prevDeleted) => [...prevDeleted, formattedNode]); 
                handleDeleteNode(node.NODE_ID.S, 'damNode', setDamNodes)
              },
            };
            newDamNodes.push(formattedNode);
          } else if (node.TYPE.S === 'device') {
            formattedNode.data = {
              ...formattedNode.data,
              onDelete: () => {
                // delete edges related to this node
                setEdges((prevEdges) => {
                  const currentDeletedEdges = prevEdges.filter((edge) => edge.source === node.NODE_ID.S || edge.target === node.NODE_ID.S);
                  setDeletedEdges((prevDeleted) => [...prevDeleted, ...currentDeletedEdges]); // Lưu edges bị xóa vào state riêng
                  return prevEdges.filter((edge) => edge.source !== node.NODE_ID.S && edge.target !== node.NODE_ID.S);
                });
                // delete node
                setDeletedNodes((prevDeleted) => [...prevDeleted, formattedNode]); 
                handleDeleteNode(node.NODE_ID.S, 'device', setDeviceNodes)
              },
            };
            newDeviceNodes.push(formattedNode);
          }

          return formattedNode;
        });

        setBranchNodes(newBranchNodes); 
        setDamNodes(newDamNodes); 
        setDeviceNodes(newDeviceNodes); 
      }
    });
  }, [id]);

  return (
    <div className="relative ml-12 mr-2 mt-2 flex h-[calc(100%-1rem)] flex-col gap-1">
      <ReactFlowProvider> log  
        <ReactFlow
          nodes={nodes}
          edges={edges}
          nodeTypes={nodeTypes}
          edgeTypes={edgeTypes}
          onNodesChange={(changes) => {
            onNodesChange(changes);
            if (changes.findIndex((change) => change.type === 'dimensions') !== -1) {
              reactFlowRef.current?.fitView({ duration: 1000 });
            }
          }}
          onEdgesChange={onEdgesChange}
          onConnect={onConnect}
          connectionMode={ConnectionMode.Loose}
          connectionLineComponent={FloatingConnectionLine}
          defaultEdgeOptions={{
            type: 'floating',
            markerEnd: {
              type: MarkerType.ArrowClosed,
              color: 'black',
              width: 6,
              height: 6,
            },
            style: {
              strokeWidth: 4.5,
            },
          }}
          deleteKeyCode={['Backspace', 'Delete']}
          className="h-[calc(100%-6rem)] border border-sky-600"
          fitView >
          <Background />
          <Controls />
        </ReactFlow>
        <ReactFlowContext ref={reactFlowRef} />
      </ReactFlowProvider>
      <div className="flex h-32 flex-wrap gap-4 overflow-y-auto border border-sky-600 p-1 px-5">
        {/* Thêm Dam Node */}
        <div
          className="h-20 w-10 cursor-pointer bg-orange-500 border border-black"
          onClick={() =>{
            const id = `dam-node-${Date.now()}`;
            addNode(
              'damNode',
              {
                id: id,
                data: {
                  label: 'Dam Node',
                  onDelete: () => handleDeleteNode(id, 'damNode', setDamNodes),
                },
              },
              setNodes,
            )
          }}
        ></div>

        {/* Thêm Branch Node */}
        <div
          className="h-10 w-10 cursor-pointer bg-blue-500 border border-black rounded-full"
          style={{ borderColor: '#356899', backgroundColor: '#356899' }}
          onClick={() => {
            const id = `branch-node-${Date.now()}`;
            addNode(
              'branchNode',
              {
                id: id,
                data: {
                  label: 'Branch Node',
                  onDelete: () => handleDeleteNode(id, 'branchNode', setBranchNodes), // Truyền hàm xóa
                },
              },
              setNodes,
            )
          }}
        ></div>

        {/* Thêm Device Node */}
        {devices.map((device) => (
          <div
            key={device.DEVICE_ID.S}
            className="flex cursor-pointer flex-col overflow-clip border border-sky-400"
            title="Click to add"
            onClick={() =>{
              addNode(
                'device',
                {
                  id: device.DEVICE_ID.S,
                  data: { 
                    ...device,
                    onDelete: () => handleDeleteNode(device.DEVICE_ID.S || '', 'device', setDeviceNodes), 
                   },
                },
                setNodes,
              )
            }}>
            <span className="inline-flex items-center justify-center whitespace-nowrap bg-sky-700 p-0.5 text-white">
              {device.DEVICE_NAME.S}
            </span>
            <div className="flex h-full flex-col justify-center px-2 py-1 text-xs">
              <span className="text-center">{device.AREA_NAME.S}</span>
              <span className="text-center">{device.RIVER_NAME.S}</span>
            </div>
          </div>
        ))}
      </div>

      <div className="absolute right-0 mr-2 mt-2 bg-white">
        <Button
          className="rounded-none p-0"
          onClick={async () => {
            const srcDeviceIdMap = edges.reduce((result: Record<string, string[]>, edge) => {
              if (result[edge.source]) {
                result[edge.source].push(edge.target);
              } else {
                result[edge.source] = [edge.target];
              }

              return result;
            }, {});

            const promise = [];


            const nodesToSave = [
              ...nodes.map((node) => ({
                AREA_NAME: id,
                NODE_ID: node.id,
                POSITIONS: `${node.position.x},${node.position.y}`,
                DATA: node.data,
                TYPE: node.type,
                CREATED_DATE: { S: Date.now() },
              })),
            ];

            promise.push(
              fput(`/api/areas/nodes/${id}`, {
                body: {
                  nodes: nodesToSave,
                },
              }),
            );

            // Save edges
            const edgesToSave = edges.map((edge) => ({
              AREA_NAME: id,
              EDGE_ID: edge.id,
              SOURCE: edge.source,
              TARGET: edge.target,
            }));
            promise.push(
              fput(`/api/areas/edges/${id}`, {
                body: {
                  edges: edgesToSave,
                },
              }),
            );

            // Save deleted nodes
            if (deletedNodes.length > 0) {
              const deletedNodesToSave = deletedNodes.map((node) => ({
                AREA_NAME: id,
                NODE_ID: node.id,
                POSITIONS: node.position,
                DATA: node.data,
                TYPE: node.type,
              }));

              console.log('deletedNodesToSave', deletedNodesToSave);
              
              promise.push(
                fdelete(`/api/areas/nodes/${id}`, {
                  body: {
                    nodes: deletedNodesToSave,
                  },
                }),
              );
            }

            // Save deleted edges
            if (deletedEdges.length > 0) {
              const deletedEdgesToSave = deletedEdges.map((edge) => ({
                AREA_NAME: id,
                EDGE_ID: edge.id,
                SOURCE: edge.source,
                TARGET: edge.target,
              }));
              promise.push(
                fdelete(`/api/areas/edges/${id}`, {
                  body: {
                    edges: deletedEdgesToSave,
                  },
                }),
              );
            }

            await Promise.allSettled(promise);

            window.location.reload();
          }}>
          Save
        </Button>
      </div>
    </div>
  );
};

export default AreaFlowChartPage;


